/**
 * @zendir/ui - Container/Card Component
 *
 * Container/Card wrapper following Astro UX Design System.
 * Automatically switches to glassmorphic styling when transparent themes are active.
 *
 * Transparent themes (transparent, transparent-bold):
 * - Glassmorphic panels with blur effect
 * - Colored top border accent
 * - Hover glow effect (like GlassCard)
 * - transparent-bold has 2px top border
 *
 * @example
 * ```tsx
 * <Container>Simple container</Container>
 * <Container title="Spacecraft Status" status="normal">Content</Container>
 * <Container title="Power System" icon="solar" iconStatus="normal">With status</Container>
 * <Container title="Custom Color" icon="thermal" iconColor="#FF6B6B">Custom color</Container>
 * ```
 */

import React, { memo, useCallback } from "react";
import { useTheme } from "../../theme";
import { classNames, addAlpha, type StatusLevel } from "../../utils";
import { AstroIcon, type AstroIconName } from "./AstroIcon";
import { HeaderIconWithStatus } from "./HeaderIconWithStatus";
import { CardHeader } from "./CardHeader";
import { useBreakpoint } from "../layout/useBreakpoint";

export type ContainerVariant =
  | "default"
  | "elevated"
  | "outlined"
  | "ghost"
  | "glass"
  | "glass-dark"
  | "glass-light";
export type ContainerPadding = "none" | "sm" | "md" | "lg";

/** Status descriptions for tooltips (default synonyms from AstroUXDS) */
const STATUS_DESCRIPTIONS: Record<StatusLevel, string> = {
  off: "Off, unavailable, disabled",
  standby: "Standby, available, enabled",
  normal: "Normal, ok, satisfactory",
  caution: "Caution, warning, unstable",
  serious: "Serious, error, needs attention",
  critical: "Critical, emergency, urgent",
};

/** Status symbol shapes following AstroUXDS guidelines */
type _StatusShape =
  | "circle"
  | "square"
  | "diamond"
  | "triangle"
  | "circle-hollow";

export interface ContainerProps {
  /** Visual variant */
  variant?: ContainerVariant;
  /** Padding size */
  padding?: ContainerPadding;
  /** Container title */
  title?: React.ReactNode;
  /** Subtitle or description */
  subtitle?: React.ReactNode;
  /** Status indicator (affects left border in non-glass mode) */
  status?: StatusLevel;
  /** Header actions (buttons, etc.) */
  actions?: React.ReactNode;
  /** Full height */
  fullHeight?: boolean;
  /**
   * Clip overflowing content area when fullHeight is enabled.
   * Keep false for form cards so wrapped rows can expand naturally.
   */
  clipContent?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Children */
  children: React.ReactNode;
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
  /** Click handler */
  onClick?: () => void;
  /** Disable auto-glass effect in transparent theme */
  disableAutoGlass?: boolean;
  /** Icon to display in header (Astro icon name) */
  icon?: AstroIconName;
  /** Icon status - shows status symbol and colors icon accordingly */
  iconStatus?: StatusLevel;
  /** Custom status message shown in tooltip (overrides default description) */
  statusMessage?: string;
  /** Custom icon color (overrides iconStatus and theme color) */
  iconColor?: string;
  /** Custom accent color for glass mode (title, line, etc.) */
  accentColor?: string;
}

/**
 * Icon wrapper with status tooltip - triggers tooltip on hover over entire icon area
 */
function IconWithStatusTooltip({
  icon,
  iconColor,
  iconStatus,
  statusMessage,
  iconSize = 20,
}: {
  icon: string;
  iconColor: string;
  iconStatus?: StatusLevel;
  statusMessage?: string;
  iconSize?: number;
}): React.ReactElement {
  const [isHovered, setIsHovered] = React.useState(false);
  const { tokens } = useTheme();
  const color = iconStatus ? tokens.colors.status[iconStatus] : iconColor;
  const defaultMessage = iconStatus ? STATUS_DESCRIPTIONS[iconStatus] : "";
  const tooltipText = statusMessage || defaultMessage;
  const statusLabel = iconStatus
    ? iconStatus.charAt(0).toUpperCase() + iconStatus.slice(1)
    : "";

  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        cursor: iconStatus ? "help" : "default",
      }}
      onMouseEnter={() => iconStatus && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Use HeaderIconWithStatus for consistent sizing with SpacecraftCard */}
      {iconStatus ? (
        <HeaderIconWithStatus
          icon={icon as AstroIconName}
          status={iconStatus}
          size={iconSize}
          statusColor={iconColor}
        />
      ) : (
        <AstroIcon name={icon} size={iconSize} color={iconColor} />
      )}
      {/* Tooltip popup - triggers on whole icon area hover */}
      {iconStatus && isHovered && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 12px)",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "8px 12px",
            backgroundColor: "rgba(10, 15, 25, 0.95)",
            border: `1px solid ${color}`,
            borderRadius: "4px",
            boxShadow: `0 0 12px ${color}40, 0 4px 12px rgba(0,0,0,0.4)`,
            zIndex: 1000,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            minWidth: "150px",
          }}
        >
          {/* Status label */}
          <div
            style={{
              fontSize: "0.6875rem", // 11px in rem
              fontWeight: 500, // AstroUXDS medium for labels
              color: color,
              marginBottom: "4px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {statusLabel}
          </div>
          {/* Status message */}
          <div
            style={{
              fontSize: "0.75rem", // 12px in rem
              color: "rgba(255,255,255,0.9)",
              lineHeight: 1.4,
            }}
          >
            {tooltipText}
          </div>
          {/* Arrow */}
          <div
            style={{
              position: "absolute",
              bottom: "-6px",
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
              width: "10px",
              height: "10px",
              backgroundColor: "rgba(10, 15, 25, 0.95)",
              borderRight: `1px solid ${color}`,
              borderBottom: `1px solid ${color}`,
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Renders AstroUXDS-compliant status symbol with proper shape
 * Shapes: Critical=triangle, Serious=diamond, Caution=square, Normal=circle, Standby=ring, Off=small-circle
 */
function StatusSymbol({
  status,
  size = 8,
  message,
  showTooltip = false,
}: {
  status: StatusLevel;
  size?: number;
  message?: string;
  showTooltip?: boolean;
}): React.ReactElement {
  const [isHovered, setIsHovered] = React.useState(false);
  const { tokens } = useTheme();
  const color = tokens.colors.status[status];
  const defaultMessage = STATUS_DESCRIPTIONS[status];
  const tooltipText = message || defaultMessage;
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);

  // Shape rendering based on Astro UX dual-coded status levels
  const renderShape = () => {
    const h = size / 2;
    const glow = `drop-shadow(0 0 3px ${color})`;
    switch (status) {
      case "critical":
        // Triangle pointing down
        return (
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            style={{ filter: glow }}
          >
            <polygon points={`${h},${size} 0,0 ${size},0`} fill={color} />
          </svg>
        );
      case "serious":
        // Diamond (rotated square)
        return (
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            style={{ filter: glow }}
          >
            <polygon
              points={`${h},0 ${size},${h} ${h},${size} 0,${h}`}
              fill={color}
            />
          </svg>
        );
      case "caution":
        // Filled square
        return (
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            style={{ filter: glow }}
          >
            <rect width={size} height={size} fill={color} />
          </svg>
        );
      case "normal":
        // Filled circle
        return (
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            style={{ filter: glow }}
          >
            <circle cx={h} cy={h} r={h} fill={color} />
          </svg>
        );
      case "standby":
        // Ring (donut) — per official Astro UXDS
        return (
          <svg
            width={size}
            height={size}
            viewBox="0 0 12 12"
            style={{ filter: glow }}
          >
            <circle
              cx="6"
              cy="6"
              r="3.5"
              fill="none"
              stroke={color}
              strokeWidth="2"
            />
          </svg>
        );
      case "off":
      default:
        // Small filled circle — per official Astro UXDS
        return (
          <svg
            width={size}
            height={size}
            viewBox="0 0 12 12"
            style={{ filter: glow }}
          >
            <circle cx="6" cy="6" r="3" fill={color} />
          </svg>
        );
    }
  };

  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: showTooltip ? "help" : "default",
      }}
      onMouseEnter={() => showTooltip && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {renderShape()}
      {/* Custom tooltip popup */}
      {showTooltip && isHovered && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "8px 12px",
            backgroundColor: "rgba(10, 15, 25, 0.95)",
            border: `1px solid ${color}`,
            borderRadius: "4px",
            boxShadow: `0 0 12px ${color}40, 0 4px 12px rgba(0,0,0,0.4)`,
            zIndex: 1000,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            minWidth: "150px",
          }}
        >
          {/* Status label */}
          <div
            style={{
              fontSize: "0.6875rem", // 11px in rem
              fontWeight: 500, // AstroUXDS medium for labels
              color: color,
              marginBottom: "4px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {statusLabel}
          </div>
          {/* Status message */}
          <div
            style={{
              fontSize: "0.75rem", // 12px in rem
              color: "rgba(255,255,255,0.9)",
              lineHeight: 1.4,
            }}
          >
            {tooltipText}
          </div>
          {/* Arrow */}
          <div
            style={{
              position: "absolute",
              bottom: "-6px",
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
              width: "10px",
              height: "10px",
              backgroundColor: "rgba(10, 15, 25, 0.95)",
              borderRight: `1px solid ${color}`,
              borderBottom: `1px solid ${color}`,
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * StatusBadge - Compact status indicator for accent lines
 * Shows colored badge with status shape at end of accent line
 */
function StatusBadge({
  status,
  tokens: _tokens,
  size = "bold",
}: {
  status: StatusLevel;
  tokens: any;
  size?: "bold" | "minimal";
}): React.ReactElement {
  const { tokens } = useTheme();
  const color = tokens.colors.status[status];
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  const isBold = size === "bold";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: isBold ? "6px" : "4px",
        padding: isBold ? "3px 10px" : "2px 8px",
        borderRadius: "12px",
        fontSize: isBold ? "11px" : "10px",
        fontWeight: 500,
        backgroundColor: `${color}20`,
        color: color,
        flexShrink: 0,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
      }}
    >
      {/* Status shape */}
      <StatusSymbol status={status} size={isBold ? 6 : 5} />
      {label}
    </span>
  );
}

export const Container = memo(function Container({
  variant = "default",
  padding = "md",
  title,
  subtitle,
  status,
  actions,
  fullHeight = false,
  clipContent = true,
  loading = false,
  children,
  className = "",
  style,
  onClick,
  disableAutoGlass = false,
  icon,
  iconStatus,
  statusMessage,
  iconColor,
  accentColor,
}: ContainerProps): React.ReactElement {
  const { tokens, theme } = useTheme();
  const { isMobile } = useBreakpoint();

  // Theme detection
  const _isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const isBoldVariant = theme === "transparent-bold";
  const isMinimalVariant = theme === "transparent-minimal";

  // Glass mode with accent styling ONLY for bold/minimal themes
  // Normal "transparent" (Glass) theme uses simpler styling without accent borders
  const useGlassMode =
    (isBoldVariant || isMinimalVariant) &&
    !disableAutoGlass &&
    !variant.startsWith("glass");

  // Simple glass mode for normal "transparent" theme (no accent borders)
  const useSimpleGlass =
    theme === "transparent" &&
    !disableAutoGlass &&
    !variant.startsWith("glass");

  // Default accent color: bold/minimal themes use theme accent (Zendir purple)
  const defaultAccentColor = tokens.colors.accent.primary;
  const glassAccentColor = accentColor || defaultAccentColor;
  const glassAccentMuted = `${glassAccentColor}66`;
  const glassGradient = `linear-gradient(135deg, ${glassAccentColor}10 0%, ${glassAccentColor}03 100%)`;

  // Determine icon color: custom > status > accent
  const resolvedIconColor =
    iconColor ||
    (iconStatus ? tokens.colors.status[iconStatus] : glassAccentColor);
  const _iconColorMuted = `${resolvedIconColor}66`;

  const paddingConfig = {
    none: "0",
    sm: isMobile ? tokens.spacing.xs : tokens.spacing.sm,
    md: isMobile ? tokens.spacing.sm : tokens.spacing.md,
    lg: isMobile ? tokens.spacing.md : tokens.spacing.lg,
  };

  const headingConfig = tokens.layout?.card?.heading ?? {
    iconSize: 20,
    iconSizeCompact: 16,
    titleFontSize: "1.125rem",
    titleFontSizeChart: "0.875rem",
    titleFontWeight: 500,
    gap: 8,
  };

  const variantStyles: Record<ContainerVariant, React.CSSProperties> = {
    default: {
      backgroundColor: tokens.colors.background.surface,
      border: `1px solid ${tokens.colors.border.muted}`,
      boxShadow: "none",
    },
    elevated: {
      backgroundColor: tokens.colors.background.surface,
      border: "none",
      boxShadow: tokens.shadows.md,
    },
    outlined: {
      backgroundColor: "transparent",
      border: `1px solid ${tokens.colors.border.muted}`,
      boxShadow: "none",
    },
    ghost: {
      backgroundColor: "transparent",
      border: "none",
      boxShadow: "none",
    },
    // Glass variants - transparent with blur
    glass: {
      backgroundColor: "rgba(15, 20, 35, 0.85)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      border: "1px solid rgba(0, 212, 255, 0.25)",
      boxShadow: "none",
    } as React.CSSProperties,
    "glass-dark": {
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      border: "1px solid rgba(100, 130, 160, 0.2)",
      boxShadow: "none",
    } as React.CSSProperties,
    "glass-light": {
      backgroundColor: "rgba(255, 255, 255, 0.15)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      border: "1px solid rgba(255, 255, 255, 0.25)",
      boxShadow: "none",
    } as React.CSSProperties,
  };

  // Glow color for hover effect (like GlassCard)
  const glowColor = accentColor ? `${accentColor}66` : "rgba(0, 212, 255, 0.4)";

  // Glass mode overrides for BOLD/MINIMAL themes (Glassmorphic Panels with accent borders)
  const glassOverrides: React.CSSProperties = useGlassMode
    ? ({
        backgroundColor: "rgba(10, 15, 25, 0.35)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${glassAccentMuted}`,
        borderTop: isBoldVariant
          ? `2px solid ${glassAccentColor}`
          : `1px solid ${glassAccentColor}`,
        transition: "all 0.3s ease-out, box-shadow 0.3s ease-out",
      } as React.CSSProperties)
    : {};

  // Simple glass overrides for normal "transparent" (Glass) theme — match top cards (ISS): transparent + blur
  const simpleGlassOverrides: React.CSSProperties = useSimpleGlass
    ? ({
        backgroundColor: "transparent",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: `1px solid ${tokens.colors.border.muted}`,
        transition: "all 0.3s ease-out",
      } as React.CSSProperties)
    : {};

  // Hover handlers for glow effect (like GlassCard)
  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (useGlassMode) {
        e.currentTarget.style.boxShadow = `0 0 30px ${glowColor}, 0 4px 20px rgba(0,0,0,0.3)`;
      }
    },
    [useGlassMode, glowColor],
  );

  const handleMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (useGlassMode) {
        e.currentTarget.style.boxShadow = "none";
      }
    },
    [useGlassMode],
  );

  const statusColor = status ? tokens.colors.status[status] : undefined;
  const isInteractive = !!onClick;

  return (
    <div
      className={classNames(
        "zendir-container",
        `zendir-container--${variant}`,
        useGlassMode && "zendir-container--auto-glass",
        className,
      )}
      onClick={onClick}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        borderRadius: tokens.borderRadius.lg,
        overflow: "hidden",
        position: "relative",
        height: fullHeight ? "100%" : "auto",
        display: "flex",
        flexDirection: "column",
        cursor: isInteractive ? "pointer" : "default",
        transition: "all 0.3s ease-out",
        borderLeft: statusColor ? `4px solid ${statusColor}` : undefined,
        ...variantStyles[variant],
        ...simpleGlassOverrides, // Normal Glass theme
        ...glassOverrides, // Bold/Minimal themes (overrides simpleGlass if both)
        ...style,
      }}
    >
      {/* Glass gradient overlay for bold/minimal transparent themes */}
      {useGlassMode && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: glassGradient,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      {/* Loading overlay */}
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: addAlpha(tokens.colors.background.surface, 0.5),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            style={{ animation: "spin 1s linear infinite" }}
          >
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke={tokens.colors.accent.primary}
              strokeWidth="3"
              strokeDasharray="31.4 31.4"
              strokeDashoffset="10"
            />
          </svg>
          <style>
            {`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
          </style>
        </div>
      )}

      {/* Header - Glass mode BOLD variant (matching GlassCard style exactly) */}
      {(title || actions) && useGlassMode && isBoldVariant && (
        <div
          style={{
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Title row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: `16px ${paddingConfig[padding]} 0`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: headingConfig.gap,
              }}
            >
              {/* Icon with optional status symbol - full area triggers tooltip */}
              {icon && (
                <IconWithStatusTooltip
                  icon={icon}
                  iconColor={resolvedIconColor}
                  iconStatus={iconStatus}
                  statusMessage={statusMessage}
                  iconSize={headingConfig.iconSize}
                />
              )}
              <h3
                style={{
                  margin: 0,
                  fontSize: headingConfig.titleFontSize,
                  fontWeight: headingConfig.titleFontWeight,
                  color: glassAccentColor,
                  letterSpacing: "0.5px",
                  textShadow: `0 0 20px ${glassAccentMuted}`,
                }}
              >
                {title}
              </h3>
            </div>
            {actions && (
              <div style={{ display: "flex", gap: tokens.spacing.xs }}>
                {actions}
              </div>
            )}
          </div>

          {subtitle && (
            <p
              style={{
                margin: 0,
                padding: `${tokens.spacing.xs} ${paddingConfig[padding]} 0`,
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.secondary,
              }}
            >
              {subtitle}
            </p>
          )}

          {/* Accent line with optional status badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              padding: `12px ${paddingConfig[padding]} 0`,
              marginBottom: tokens.spacing.sm,
            }}
          >
            {/* Main accent line with gradient */}
            <div
              style={{
                flex: 1,
                height: "2px",
                background: `linear-gradient(90deg, ${glassAccentColor} 0%, ${glassAccentMuted} 70%, ${glassAccentMuted}20 100%)`,
                boxShadow: `0 0 8px ${glassAccentMuted}`,
              }}
            />
            {/* Status badge at the end if status is set */}
            {iconStatus && iconStatus !== "off" && (
              <StatusBadge status={iconStatus} tokens={tokens} size="bold" />
            )}
          </div>
        </div>
      )}

      {/* Header - Glass mode MINIMAL variant (inline thin line) */}
      {(title || actions) && useGlassMode && !isBoldVariant && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: `${tokens.spacing.sm} ${paddingConfig[padding]} 0`,
            marginBottom: tokens.spacing.sm,
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Icon with optional status symbol - full area triggers tooltip */}
          {icon && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: tokens.spacing.sm,
              }}
            >
              <IconWithStatusTooltip
                icon={icon}
                iconColor={resolvedIconColor}
                iconStatus={iconStatus}
                statusMessage={statusMessage}
                iconSize={headingConfig.iconSizeCompact}
              />
            </div>
          )}

          {/* Title — card heading system */}
          {title && (
            <h3
              style={{
                margin: 0,
                fontSize: headingConfig.titleFontSize,
                fontWeight: headingConfig.titleFontWeight,
                color: glassAccentColor,
                textShadow: `0 0 16px ${glassAccentMuted}`,
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
                marginRight: "12px",
              }}
            >
              {title}
            </h3>
          )}

          {/* Accent line - inline after title with optional status badge */}
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
              minWidth: 40,
            }}
          >
            {/* Main gradient line */}
            <div
              style={{
                flex: 1,
                height: "1px",
                background: `linear-gradient(90deg, ${glassAccentColor} 0%, ${glassAccentMuted} 70%, ${glassAccentMuted}20 100%)`,
              }}
            />
            {/* Status badge at the end if status is set */}
            {iconStatus && iconStatus !== "off" && (
              <StatusBadge status={iconStatus} tokens={tokens} size="minimal" />
            )}
          </div>

          {actions && (
            <div
              style={{
                display: "flex",
                gap: tokens.spacing.xs,
                flexShrink: 0,
                marginLeft: "12px",
              }}
            >
              {actions}
            </div>
          )}
        </div>
      )}

      {/* Subtitle for glass mode MINIMAL - separate row */}
      {subtitle && useGlassMode && !isBoldVariant && (
        <p
          style={{
            margin: 0,
            padding: `0 ${paddingConfig[padding]}`,
            marginBottom: tokens.spacing.sm,
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.text.secondary,
            position: "relative",
            zIndex: 1,
          }}
        >
          {subtitle}
        </p>
      )}

      {/* Header - Regular mode (non-glass) - Uses shared CardHeader for consistency */}
      {(title || actions) && !useGlassMode && (
        <div
          style={{
            padding: `${paddingConfig[padding]} ${paddingConfig[padding]} 0`,
            position: "relative",
            zIndex: 1,
          }}
        >
          <CardHeader
            icon={icon || (iconStatus ? "mission" : "")}
            title={typeof title === "string" ? title : String(title || "")}
            subtitle={typeof subtitle === "string" ? subtitle : undefined}
            status={iconStatus || status}
            iconColor={iconColor}
            iconSize={headingConfig.iconSize}
            hideStatusIndicator={!icon && !iconStatus && !status}
            rightSlot={actions}
            style={{ marginBottom: tokens.spacing.sm }}
          />
          {/* Render non-string subtitles (React elements) below the CardHeader */}
          {subtitle && typeof subtitle !== "string" && (
            <div
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.secondary,
                marginTop: -parseInt(tokens.spacing.xs || "4", 10) || -4,
                marginBottom: parseInt(tokens.spacing.sm || "8", 10) || 8,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div
        style={{
          flex: 1,
          minHeight: fullHeight ? 0 : undefined,
          overflow: fullHeight && clipContent ? "hidden" : undefined,
          // When fullHeight, become a flex column so any child using flex:1 or height:100%
          // gets a properly bounded height and scroll containers can work.
          display: fullHeight ? "flex" : undefined,
          flexDirection: fullHeight ? "column" : undefined,
          padding: fullHeight
            ? `${tokens.spacing.xs} 0 0 0`
            : title
              ? `${tokens.spacing.xs} ${paddingConfig[padding]} ${paddingConfig[padding]}`
              : paddingConfig[padding],
          position: "relative",
          zIndex: 1,
        }}
      >
        {children}
      </div>
    </div>
  );
});

export default Container;
